export default class UApi {
  url: string;

  windowOrNodeFetch: typeof fetch;

  constructor({ url, windowOrNodeFetch }: { url?: string; windowOrNodeFetch?: typeof fetch }) {
    if (!windowOrNodeFetch && typeof window === 'undefined') {
      throw Error(
        'An instance of node/svelte fetch must be provided for server side UApi initialization.'
      );
    }
    this.windowOrNodeFetch = windowOrNodeFetch || fetch;

    url = url || import.meta.env.VITE_API_URL || '';
    if (url[url.length - 1] === '/') {
      url = url.slice(0, -1);
    }
    this.url = url;
  }

  getFullUrl(path: string): string {
    // if (!path) return this.url;
    if (path[0] !== '/') path = `/${path}`;
    return this.url + path;
  }

  post<T>(
    path = '',
    payload: any = {},
    method = 'POST',
    contentType = 'application/json'
  ): Promise<T> {
    const isJSON = contentType.toLowerCase().search('json') !== -1;
    const body = isJSON ? JSON.stringify(payload) : payload;

    return this.windowOrNodeFetch(this.getFullUrl(path), {
      headers: {
        Accept: 'application/json',
        'Content-Type': contentType,
      },
      credentials: 'include',
      method,
      body,
    })
      .then(status)
      .then(json);
  }

  upload<T>(path = '', imageField: HTMLInputElement): Promise<T> {
    const image = imageField.files?.[0];
    if (!image) return Promise.reject('Did not get image to upload.');
    const formData = new window.FormData();
    formData.append('file', image);

    return this.windowOrNodeFetch(this.getFullUrl(path), {
      headers: {
        Accept: 'application/json',
      },
      credentials: 'include',
      method: 'post',
      body: formData,
    })
      .then(status)
      .then(json);
  }

  put<T>(path = '', payload: any = {}, contentType?: string): Promise<T> {
    return this.post(path, payload, 'PUT', contentType);
  }

  get<T>(
    path = '',
    queryParams?: Record<string, string>,
    method = 'GET',
    headers: { [key: string]: string } = {}
  ): Promise<T> {
    const uriEncoded = uriEncode(queryParams || {});
    return this.windowOrNodeFetch(this.getFullUrl(path) + uriEncoded, {
      method,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'If-Modified-Since': 'Mon, 26 Jul 1997 05:00:00 GMT',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        ...headers,
      },
      cache: 'reload',
    })
      .then(status)
      .then(json);
  }

  delete<T>(path = '', queryParams?: Record<string, string>): Promise<T> {
    return this.get(path, queryParams, 'DELETE');
  }
}

function json(response: Response) {
  if (response && typeof response === 'object' && response.json) {
    return response.json().catch(() => ({
      status: response.status,
      message: response.statusText,
      response,
    }));
  }
  return Promise.resolve(response);
}

function status(response: Response) {
  if (response.ok) {
    return Promise.resolve(response);
  }

  return json(response).then((jsonObj) => {
    // eslint-disable-next-line no-param-reassign
    jsonObj.status = response.status;
    return Promise.reject(jsonObj);
  });
}

/**
 * Encore an options object as parameters in a query object. Returns ?width=234&height=126 type string.
 * @param {Object} params
 * @returns {string}
 */
export function uriEncode(params: Record<string, string>): string {
  const urlParams = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    urlParams.set(key, params[key]);
  });
  return `?${urlParams.toString()}`;
}
